import React, { useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';
import { MdDownload } from 'react-icons/md';
import { FaEye } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import COLORS from '../styles/COLORS';
import { statusBar } from '../../utils';
import CustomTooltip from '../userProjects/customTooltip';

// Cell renderer for Download button
const DownloadCellRenderer = (props) => {
  const handleDownload = (e) => {
    e.preventDefault();
    if (!props.data) return;
    const link = props.data.description;
    if (!link || link.trim() === '') {
      toastr.error('No link found');
      return;
    }
    window.location.href = `${link}${link.includes('?') ? '&' : '?'}download=1`;
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <button
        onClick={handleDownload}
        style={{
          background: 'none',
          border: 'none',
          color: COLORS.green,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          padding: 0,
        }}
      >
        <MdDownload style={{ fontSize: '20px' }} />
      </button>
    </div>
  );
};

const ViewCellRenderer = (props) => {
  const handleView = (e) => {
    e.preventDefault();
    if (!props.data) return;
    const link = props.data.description;
    if (!link || link.trim() === '') {
      toastr.error('No link found');
      return;
    }
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <button
        onClick={handleView}
        style={{
          background: 'none',
          border: 'none',
          color: COLORS.green,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          padding: 0,
        }}
      >
        <FaEye style={{ fontSize: '18px' }} />
      </button>
    </div>
  );
};

/**
 * Templates - Component for displaying template files with download and preview options
 */
const UserGuide = () => {
  const userGuides = useSelector((state) => state.ctrl.userGuides);
  const gridRef = useRef();

  // Configure toastr
  toastr.options = {
    positionClass: 'toast-top-right',
    closeButton: true,
    progressBar: false,
    timeOut: 3000,
  };

  const columnDefs = useMemo(() => [
    {
      headerName: 'Name',
      field: 'meaning',
      flex: 1,
      resizable: true,
      minWidth: 250,
      tooltipField: 'meaning',
      tooltipComponent: CustomTooltip,
      tooltipComponentParams: { field: '9' },
    },
    {
      headerName: 'Start Date',
      field: 'start_date_active',
      flex: 1,
      filter: true,
      resizable: true,
      minWidth: 150,
      valueFormatter: (params) => {
        if (!params.value) return '';
        const date = new Date(params.value);
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        });
      },
    },
    {
      headerName: 'Download',
      field: 'download',
      flex: 1,
      filter: false,
      sortable: false,
      cellRenderer: DownloadCellRenderer,
      suppressMenu: true,
    },
    {
      headerName: 'View',
      field: 'view',
      flex: 1,
      filter: false,
      sortable: false,
      cellRenderer: ViewCellRenderer,
      suppressMenu: true,
    },
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    filter: false,
  }), []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        p: 2,
        height: 'calc(100vh - 120px)',
        width: '100%',
      }}
    >
      {userGuides && userGuides.length > 0 ? (
        <div className="ag-theme-alpine" style={{ height: '100%', width: '100%' }}>
          <AgGridReact
            ref={gridRef}
            rowData={userGuides}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            headerHeight={30}
            rowHeight={25}
            statusBar={statusBar}
            animateRows={true}
            suppressCopyRowsToClipboard={true}
            tooltipShowDelay={0}
            tooltipMouseTrack={true}
          />
        </div>
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
          }}
        >
          <span style={{ color: '#999', fontSize: '16px' }}>No user guides available</span>
        </Box>
      )}
    </Box>
  );
};

export default UserGuide;
